import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Plus, Calendar, Flag, Tag, Home, User, Briefcase } from 'lucide-react'
import { useTasks } from '../../features/taskManagement'
import './QuickAddWindow.scss'

interface QuickAddWindowProps {
  lifeSpace?: 'personal' | 'work' | 'home'
}

export const QuickAddWindow: React.FC<QuickAddWindowProps> = ({ lifeSpace: initialLifeSpace }) => {
  const { t } = useTranslation()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [attention, setAttention] = useState<'low' | 'medium' | 'high'>('medium')
  const [dueDate, setDueDate] = useState('')
  const [lifeSpace, setLifeSpace] = useState<'personal' | 'work' | 'home'>(initialLifeSpace || 'personal')
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  const titleInputRef = useRef<HTMLInputElement>(null)
  const { createTask } = useTasks()

  // Get window info from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.split('?')[1] || '')
    const data = params.get('data')
    if (data) {
      try {
        const parsed = JSON.parse(decodeURIComponent(data))
        if (parsed.lifeSpace) {
          setLifeSpace(parsed.lifeSpace)
        }
      } catch (e) {
        console.error('Failed to parse window data:', e)
      }
    }
  }, [])

  // Auto-focus title input on mount
  useEffect(() => {
    titleInputRef.current?.focus()
  }, [])

  // Handle ESC key to close window
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose()
      }
      // Ctrl/Cmd + Enter to submit
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && title.trim()) {
        handleSubmit()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [title])

  const handleClose = () => {
    // Close the window
    if (window.windowAPI) {
      const params = new URLSearchParams(window.location.hash.split('?')[1] || '')
      const windowId = params.get('windowId')
      if (windowId) {
        window.windowAPI.closeWindow(windowId)
      }
    } else {
      window.close()
    }
  }

  const handleSubmit = async () => {
    if (!title.trim() || isCreating) return

    setIsCreating(true)
    try {
      await createTask({
        title: title.trim(),
        description: description.trim(),
        attention,
        dueDate: dueDate || undefined,
        lifeSpace,
        tags: tags.length > 0 ? tags : undefined
      })

      // Broadcast task created event
      if (window.windowAPI) {
        window.windowAPI.broadcast('task-created', {
          title: title.trim(),
          lifeSpace
        })
      }

      // Close window after successful creation
      handleClose()
    } catch (error) {
      console.error('Failed to create task:', error)
      setIsCreating(false)
    }
  }

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()])
      setTagInput('')
    }
  }

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter(t => t !== tag))
  }

  const handleTagInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddTag()
    }
  }

  const getLifeSpaceIcon = () => {
    switch (lifeSpace) {
      case 'personal': return <User size={14} />
      case 'work': return <Briefcase size={14} />
      case 'home': return <Home size={14} />
    }
  }

  const getLifeSpaceColor = () => {
    switch (lifeSpace) {
      case 'personal': return 'quick-add__life-space--personal'
      case 'work': return 'quick-add__life-space--work'
      case 'home': return 'quick-add__life-space--home'
    }
  }

  return (
    <div className="quick-add">
      <div className="quick-add__header">
        <h2 className="quick-add__title">{t('quickAdd.title')}</h2>
        <button
          className="quick-add__close"
          onClick={handleClose}
          title={t('quickAdd.closeTooltip')}
        >
          <X size={18} />
        </button>
      </div>

      <div className="quick-add__content">
        <input
          ref={titleInputRef}
          type="text"
          className="quick-add__title-input"
          placeholder={t('quickAdd.titlePlaceholder')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault()
              if (title.trim()) {
                handleSubmit()
              }
            }
          }}
        />

        <textarea
          className="quick-add__description"
          placeholder={t('quickAdd.descriptionPlaceholder')}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
        />

        <div className="quick-add__meta">
          <div className="quick-add__meta-group">
            <div className="quick-add__field">
              <Flag size={14} />
              <select
                value={attention}
                onChange={(e) => setAttention(e.target.value as 'low' | 'medium' | 'high')}
                className={`quick-add__select quick-add__priority--${attention}`}
              >
                <option value="low">{t('quickAdd.priority.low')}</option>
                <option value="medium">{t('quickAdd.priority.medium')}</option>
                <option value="high">{t('quickAdd.priority.high')}</option>
              </select>
            </div>

            <div className="quick-add__field">
              <Calendar size={14} />
              <input
                type="date"
                className="quick-add__date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div className="quick-add__field">
              <button
                className={`quick-add__life-space-btn ${getLifeSpaceColor()}`}
                onClick={() => {
                  // Cycle through life spaces
                  const spaces: Array<'personal' | 'work' | 'home'> = ['personal', 'work', 'home']
                  const currentIndex = spaces.indexOf(lifeSpace)
                  const nextIndex = (currentIndex + 1) % spaces.length
                  setLifeSpace(spaces[nextIndex])
                }}
              >
                {getLifeSpaceIcon()}
                <span>{t(`lifeSpaces.${lifeSpace}`)}</span>
              </button>
            </div>
          </div>

          <div className="quick-add__tags">
            {tags.map(tag => (
              <span key={tag} className="quick-add__tag">
                <Tag size={10} />
                {tag}
                <button
                  className="quick-add__tag-remove"
                  onClick={() => handleRemoveTag(tag)}
                >
                  <X size={10} />
                </button>
              </span>
            ))}
            <input
              type="text"
              className="quick-add__tag-input"
              placeholder={t('quickAdd.tagPlaceholder')}
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagInputKeyDown}
              onBlur={handleAddTag}
            />
          </div>
        </div>
      </div>

      <div className="quick-add__footer">
        <div className="quick-add__shortcuts">
          <span className="quick-add__shortcut">
            <kbd>Enter</kbd> {t('quickAdd.shortcuts.create')}
          </span>
          <span className="quick-add__shortcut">
            <kbd>Ctrl</kbd>+<kbd>Enter</kbd> {t('quickAdd.shortcuts.createAndClose')}
          </span>
          <span className="quick-add__shortcut">
            <kbd>ESC</kbd> {t('quickAdd.shortcuts.cancel')}
          </span>
        </div>

        <div className="quick-add__actions">
          <button
            className="quick-add__btn quick-add__btn--cancel"
            onClick={handleClose}
          >
            {t('quickAdd.cancel')}
          </button>
          <button
            className="quick-add__btn quick-add__btn--create"
            onClick={handleSubmit}
            disabled={!title.trim() || isCreating}
          >
            {isCreating ? (
              <>{t('quickAdd.creating')}</>
            ) : (
              <>
                <Plus size={14} />
                {t('quickAdd.create')}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}